/**
 * The first screen: what Wend is in one line, and the mark drawing itself.
 *
 * The owner's brief for this rebuild: "i am selling reach, connect, sense and
 * flow … i need a site in which i am showing things in action." So the
 * headline names the four parts as one loop, the subtext says what each does
 * in four verbs, and the picture is motion rather than a paragraph: the
 * animated mark, with a light travelling round it that lights each part as it
 * passes — the loop, before the reader has read a word about it.
 *
 * Two calls to action: "Try Wend" goes to the interest form (the page's one
 * ask; there is no self-serve sign-up), "See it work" to the interactive loop.
 *
 * What the subtext may say is bounded by the rest of the page's rules: "your
 * own accounts" (bring-your-own SES, number and Meta ad account) and "you
 * launch" (nothing in Wend starts an ad, a campaign or a flow on its own).
 */
import { cn } from '@wend/ui';

import { WendMarkAnimated } from '../components/WendMarkAnimated';

const ORBIT = [
  { id: 'reach', label: 'Reach', at: 'left-1/2 top-[12%] -translate-x-1/2 -translate-y-1/2', delay: '0s' },
  { id: 'connect', label: 'Connect', at: 'right-[12%] top-1/2 translate-x-1/2 -translate-y-1/2', delay: '2s' },
  { id: 'sense', label: 'Sense', at: 'bottom-[12%] left-1/2 -translate-x-1/2 translate-y-1/2', delay: '4s' },
  { id: 'flow', label: 'Flow', at: 'left-[12%] top-1/2 -translate-x-1/2 -translate-y-1/2', delay: '6s' },
] as const;

/** The mark inside its orbit. Decorative: the headline says the same thing in words. */
function HeroLoop() {
  return (
    <div aria-hidden="true" data-hero-loop="" className="relative mx-auto aspect-square w-[min(100%,300px)] sm:w-[380px]">
      {/* The track, and the light that travels it. */}
      <div className="absolute inset-[12%] rounded-full border border-dashed border-brand-b/30" />
      <div className="orbit-spin absolute inset-[12%]">
        <span className="absolute left-1/2 top-0 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-b shadow-[0_0_16px_4px_rgba(155,94,246,0.7)]" />
      </div>
      <div className="absolute inset-[28%] rounded-full bg-brand-a/25 blur-3xl" />

      <div className="absolute inset-0 grid place-items-center">
        <WendMarkAnimated size={150} speed="slow" className="sm:hidden" />
        <WendMarkAnimated size={180} speed="slow" className="hidden sm:inline-block" />
      </div>

      {ORBIT.map((part) => (
        <span key={part.id} className={cn('absolute', part.at)}>
          <span
            className="orbit-chip block rounded-full border border-line-strong bg-surface-2/95 px-3.5 py-1.5 font-mono text-[0.72rem] uppercase tracking-[0.14em] text-text"
            style={{ animationDelay: part.delay }}
          >
            {part.label}
          </span>
        </span>
      ))}
    </div>
  );
}

export function Hero() {
  return (
    <div id="top" className="glow-brand overflow-hidden border-b border-line px-5 pb-20 pt-14 sm:px-8 sm:pb-28 sm:pt-24">
      <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <h1 className="text-balance text-[2.6rem] font-semibold leading-[1.04] tracking-tight text-text sm:text-6xl lg:text-[4.1rem]">
            Reach. Connect. Sense. Flow.
            <span className="mt-2 block text-gradient-brand">One loop for every customer.</span>
          </h1>
          <p className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted sm:text-xl">
            Bring people in, talk to them, see what worked, follow up. All on your own accounts, launched by you.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#talk"
              className="inline-flex items-center justify-center rounded-base bg-brand-a px-7 py-3.5 text-[1rem] font-medium text-white shadow-brand transition-colors hover:bg-brand-b focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a"
            >
              Try Wend
            </a>
            <a
              href="#loop"
              className="inline-flex items-center justify-center gap-2 rounded-base border border-line-strong px-7 py-3.5 text-[1rem] text-text transition-colors hover:border-brand-a hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M8 5l11 7-11 7z" fill="currentColor" />
              </svg>
              See it work
            </a>
          </div>
        </div>

        <div className="py-6">
          <HeroLoop />
        </div>
      </div>
    </div>
  );
}
